import { Injectable } from '@angular/core';
import { AccountFacade } from '@mode/accounts/contract';
import {
  DatasetResult,
  FeatureFlag,
  FeatureFlagsFacade,
  ProducerTypes,
  SwitchViewDomain,
} from '@mode/shared/contract-common';
import { SwitchViewClientService } from '@mode/shared/data-access-webapp';
import { ToastService } from '@mode/shared/util-toast';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CappieReporterService } from '@mode/shared/util-cappie-reporter';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { recordEvent } from '@mode/shared/util-tracing';
import {
  FlamingoActions,
  ImportedDatasetFacade,
  SwitchViewFacade,
  SwitchViewActions,
} from '@mode/switch-views/contract';
import * as Backoff from 'backo';
import { EMPTY, of, zip } from 'rxjs';
import {
  catchError,
  combineLatestWith,
  concatMap,
  exhaustMap,
  filter,
  first,
  map,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';
import { isStillRunning } from '../data-view-helpers';
import { SwitchViewFeatureState } from './switch-view.reducer';
import { DatasetFacade, DatasetActions } from '@mode/dataset/contract';
import { SwitchViewsRankMap } from 'libs/shared/contract-common/src/lib/switch/switch-view.types';
import { CellExportActions } from '@mode/notebooks/contract';

@Injectable()
export class SwitchViewEffects {
  loadSwitchView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SwitchViewActions.loadSwitchViewDefinitonRoute),
      concatMap((action) =>
        this.switchViewService
          .getSwitchView(action.switchViewToken)
          .pipe(
            map(({ datasetResult, switchView }) =>
              SwitchViewActions.loadSwitchViewSuccess({ datasetResult, switchView })
            )
          )
      )
    )
  );

  refreshSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FlamingoActions.refreshSwitchViewSignature),
      filter((action) => action.reportOwner !== undefined && action.reportOwner !== null),
      tap((action) =>
        recordEvent('flamingo-query', 'refresh-signature', {
          traceId: action.executePlanAction.plan.id,
          refId: action.executePlanAction.refId,
          switchViewToken: action.switchViewToken,
        })
      ),
      mergeMap((action) =>
        this.switchViewService.reloadFlamingoSignature(action.reportOwner, action.switchViewToken).pipe(
          map((signature) =>
            FlamingoActions.refreshSwitchViewSignatureSuccess({
              executePlanAction: action.executePlanAction,
              signature: signature,
              switchViewToken: action.switchViewToken,
            })
          )
        )
      )
    )
  );

  /**
   * Initiate polling for the viewer.
   */
  startPolling$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SwitchViewActions.startPolling),
        tap((action) =>
          recordEvent('report-loading', 'poll-switch-views', {
            traceId: action.reportToken,
            runToken: action.reportRunId,
            tokens: action.tokens,
          })
        ),
        concatMap((action) => {
          // Begin polling for switch views until dataset is in a complete state
          return this.pollSwitchViews(action.accountId, action.reportToken, action.tokens, action.reportRunId);
        })
      );
    },
    { dispatch: false }
  );

  /**
   * Initiate polling for the editor.
   *
   * The only difference here v the viewer is how the account id is obtained. They kick off the same flows
   * and can be consolidated if they both use the new account flow in the future.
   */
  startEditorPolling$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SwitchViewActions.startEditorPolling),
        switchMap((action) =>
          this.accountFacade.reportOwner$.pipe(
            first(),
            map((account) => ({ account, action }))
          )
        ),
        switchMap(({ account, action }) =>
          this.pollSwitchViews(account.username, action.reportToken, action.switchViewTokens, action.reportRunId)
        )
      );
    },
    { dispatch: false }
  );

  deleteSwitchViewResourcesFromCache$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(SwitchViewActions.deleteSwitchViewResourcesFromCache),
        tap((action) => {
          this.switchViewService.deleteSwitchViewsFromCache(action.switchViewTokens);
        })
      );
    },
    { dispatch: false }
  );

  removeDataset$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        SwitchViewActions.removeDatasetFromDatasetTab,
        SwitchViewActions.removeDatasetFromSideNav,
        DatasetActions.removePhantomDatasetClicked
      ),
      switchMap((action) =>
        this.switchViewFacade.getEnhancedDataseResult(action.switchViewToken).pipe(
          switchMap((result) =>
            this.switchViewService.removeDataset(action.accountId, action.reportId, action.switchViewToken).pipe(
              map(() =>
                SwitchViewActions.removeDatasetSuccess({
                  switchViewToken: action.switchViewToken,
                  producerToken: result.producerToken,
                })
              ),
              catchError((err) => {
                return of(
                  SwitchViewActions.removeDatasetFailure({
                    switchViewToken: action.switchViewToken,
                    error: err.error.message,
                  })
                );
              })
            )
          )
        )
      )
    );
  });

  reorderDatasets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SwitchViewActions.reorderDatasets),
      switchMap((action) =>
        this.accountFacade.reportOwner$.pipe(
          first(),
          map((account) => ({
            account,
            action,
          }))
        )
      ),
      switchMap(({ account, action }) => {
        return this.switchViewService
          .reorderDatasets(account.username, action.reportToken, action.switchViewToken, action.prevSwitchViewToken)
          .pipe(
            map((switchViewRankMap) => {
              return SwitchViewActions.reorderDatasetsSuccess({
                switchViewsRankMap: switchViewRankMap,
              });
            }),
            catchError((err) => {
              this.toastService.error('Unable to re-order, please try again');
              return of(
                SwitchViewActions.reorderDatasetsFailure({
                  error: err.error.message,
                })
              );
            })
          );
      })
    )
  );

  loadEditorViews$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SwitchViewActions.loadEditorViews,
        CellExportActions.refreshDataViewsAfterExport,
        CellExportActions.refreshReportRunAfterCellRun
      ),
      // switchMap instead of withLatestFrom because reportOwner isn't guaranteed to have finished loading yet
      switchMap((action) =>
        this.accountFacade.reportOwner$.pipe(
          first(),
          map((account) => ({
            account,
            action,
          }))
        )
      ),
      switchMap(({ action, account }) =>
        (action.type === SwitchViewActions.loadEditorViews.type
          ? this.switchViewService.getViewsInReport(account.username, action.reportToken) // load editor views
          : this.switchViewService.refreshViewsInReport(account.username, action.reportToken)
        ) // refresh editor views
          .pipe(
            tap((dataViews) => {
              const stillRunningViewTokens = dataViews
                .filter((dataView) => isStillRunning(dataView.datasetResult.queryRunState))
                .map((dataView) => dataView.switchView.token);

              // Passing null since the reportRunToken context is not really relevant on load, and we also don't have
              // easy access to it without pulling it from the metatags.
              this.pollSwitchViews(account.username, action.reportToken, stillRunningViewTokens, null);
            }),
            map((dataViews) =>
              action.type === SwitchViewActions.loadEditorViews.type
                ? SwitchViewActions.loadEditorViewsSuccess({ reportToken: action.reportToken, dataViews })
                : SwitchViewActions.refreshEditorViewsSuccess({
                    reportToken: action.reportToken,
                    dataViews,
                    exportedDatasetToken: action.exportedDatasetToken,
                  })
            )
          )
      )
    )
  );

  refreshImportedAllDataset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SwitchViewActions.datasetRefreshAllClicked),
        exhaustMap((action) => {
          const switchViewTokens = action.switchViewTokens;
          let mostRecentReportRunData: {
            switchView: SwitchViewDomain.SwitchView;
            datasetResult: DatasetResult;
            shareLink: string;
            mostRecentReportRunToken: string;
          };
          zip(
            switchViewTokens.map((token) => this.switchViewService.refreshImportedDataset(action.reportToken, token))
          ).subscribe((results) => {
            let dataRefreshCount = 0;
            for (let response of results) {
              if (response.dataRefreshed) {
                dataRefreshCount++;
                mostRecentReportRunData = response;
                this.store.dispatch(
                  SwitchViewActions.importedDatasetRefreshed({
                    switchView: response.switchView,
                    datasetResult: response.datasetResult,
                    shareLink: response.shareLink,
                    queryToken: response.datasetResult.queryToken,
                    mostRecentReportRunToken: response.mostRecentReportRunToken,
                  })
                );
              }
            }
            if (dataRefreshCount === results.length) {
              this.store.dispatch(
                SwitchViewActions.datasetAllRefreshed({
                  ...mostRecentReportRunData,
                })
              );
              this.toastService.success('Updated to the latest data');
            } else if (dataRefreshCount === 0) {
              this.toastService.success('No updates found. You’re using the latest data.');
            }
          });
          return EMPTY;
        })
      ),
    { dispatch: false }
  );

  refreshImportedDataset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          SwitchViewActions.datasetRefreshButtonClicked,
          ImportedDatasetFacade.Actions.datasetRefreshFromContextMenu,
          ImportedDatasetFacade.Actions.datasetRefreshFromDatasetTab,
          ImportedDatasetFacade.Actions.datasetRefreshFromSideBar,
          ImportedDatasetFacade.Actions.datasetRefreshFromQueryNavContextMenu
        ),
        exhaustMap(
          (
            action // exhaustMap ignores subsequent button clicks while performing the effect
          ) =>
            this.switchViewService.refreshImportedDataset(action.reportToken, action.switchViewToken).pipe(
              map((response) => {
                if (response.dataRefreshed) {
                  this.toastService.success('Updated to the latest data');
                  this.store.dispatch(
                    SwitchViewActions.importedDatasetRefreshed({
                      switchView: response.switchView,
                      datasetResult: response.datasetResult,
                      shareLink: response.shareLink,
                      queryToken: response.datasetResult.queryToken,
                      mostRecentReportRunToken: response.mostRecentReportRunToken,
                    })
                  );
                } else {
                  this.toastService.success('No updates found. You’re using the latest data.');
                }
              }),
              catchError(() => {
                this.toastService.error('Creating Report Run Failed');
                return of(null);
              })
            )
        )
      ),
    { dispatch: false }
  );

  // Also used to handle loading of notebook cell based datasets
  loadImportedDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.loadImportedDataset),
      exhaustMap((action) => this.switchViewFacade.getEnhancedDataseResult(action.switchViewToken).pipe(first())),
      combineLatestWith(this.featureFlagsFacade.asObservable(FeatureFlag.NotebookExportDatasets)),
      map(([result, isNotebookExportDatasetsEnabled]) => {
        if (isNotebookExportDatasetsEnabled && result.producerType === ProducerTypes.NotebookCell) {
          return DatasetActions.loadNotebookCellDatasetEditRoute({
            datasetToken: result.datasetToken,
            switchViewToken: result.switchViewToken,
          });
        } else {
          return DatasetActions.loadDatasetReportEditRoute({
            datasetToken: result.datasetToken,
          });
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private switchViewService: SwitchViewClientService,
    private accountFacade: AccountFacade,
    private datasetFacade: DatasetFacade,
    private store: Store<SwitchViewFeatureState>, // Temporary in order to be able to dispatch additional actions from one effect
    private toastService: ToastService,
    private observabilityService: ObservabilityService,
    private cappieReporterService: CappieReporterService,
    private switchViewFacade: SwitchViewFacade,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    this.currentReportRunId = null;
  }

  currentReportRunId: string | null;

  /**
   * Start polling switchViews.
   *
   * This polling helper is located here until the polling behavior is fleshed out more and it can be refactored into more
   * appropriately.
   *
   * Notes
   *  - If `reportRunToken` is null, we get poll the `last_run` state. This means, `reportRunToken` should only be
   *    null from the editor context. The viewer context is typed to explicitly pass a `reportRunToken` always.
   *  - `tokens` at this point should only even represent local switch views. Currently in our behavioral flows,
   *    "polling" doesn't make sense for imported switch views, and additionally, imported switch views will cause
   *    an error with the polling endpoint.
   */
  private async pollSwitchViews(
    accountId: string,
    reportToken: string,
    tokens: ReadonlyArray<string>,
    reportRunId: string | null
  ): Promise<void> {
    const cappieReporter = this.cappieReporterService.progressEventAPI('switch-view-polling');
    cappieReporter.start('starting switch view polling');
    this.observabilityService.startChildSpan(ObservabilityEvents.MODERN_POLLING_LOOP, ObservabilityEvents.QUERY_RUN);

    this.currentReportRunId = reportRunId;

    let queue = [...tokens];
    const startTime = Date.now();

    // @ts-ignore - Typescript has problems with the import type of the Backoff module legacy syntax.
    const backoff = new Backoff({ min: 50, max: 5000, factor: 1.2 });

    // Continue the polling if there are items left and a new report run has not been created.
    while (queue.length > 0 && this.currentReportRunId === reportRunId) {
      const unfinishedQueries = [];

      for (let token of queue) {
        this.observabilityService.startChildSpan(
          ObservabilityEvents.SINGLE_QUERY + `_${token}`,
          ObservabilityEvents.QUERY_RUN,
          startTime,
          {
            query_run_token: token,
          }
        );
        recordEvent('report-loading', 'poll-switch-view', {
          traceId: reportToken,
          runToken: reportRunId,
          switchViewToken: token,
        });

        // Converting to promise is not ideal, but don't want to refactor right now
        const simpleState = await this.switchViewService
          .getSwitchViewRunState(accountId, reportToken, token, reportRunId)
          .toPromise()
          .catch((err) => {
            recordEvent('report-loading', 'poll-switch-view-failed', {
              traceId: reportToken,
              runToken: reportRunId,
              switchViewToken: token,
              err,
            });

            return null;
          }); // catch any possible request error and set simpleState to null

        if (simpleState) {
          if (isStillRunning(simpleState.state)) {
            unfinishedQueries.push(token);
          } else {
            recordEvent('report-loading', 'get-switch-view-result', {
              traceId: reportToken,
              runToken: reportRunId,
              switchViewToken: token,
              state: simpleState.state,
            });

            const dataViewResult = await (this.switchViewService
              .getSwitchViewWithResult(accountId, token, reportRunId)
              .toPromise() as unknown as Promise<SwitchViewDomain.DataView>);
            this.store.dispatch(SwitchViewActions.pollingSwitchViewCompleted({ dataView: dataViewResult }));
            this.datasetFacade.runDatasetPollingCompleted({ dataView: dataViewResult });
            this.observabilityService.endChildSpan(ObservabilityEvents.SINGLE_QUERY + `_${token}`);
            recordEvent('report-loading', 'poll-switch-view-success', {
              traceId: reportToken,
              runToken: reportRunId,
              switchViewToken: token,
              dataView: dataViewResult,
              state: simpleState.state,
            });
          }
        }
      }

      queue = unfinishedQueries;

      if (unfinishedQueries.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, backoff.duration()));
        cappieReporter.progress('continue switch view polling');
      }
    }

    cappieReporter.end('finished switch view polling');
    this.observabilityService.endChildSpan(ObservabilityEvents.MODERN_POLLING_LOOP);
  }
}
